export { default as ParentSize } from '@visx/responsive/lib/components/ParentSize';
import React__default, { useMemo, useCallback, useRef, useEffect, useState, createElement } from 'react';
import Pie from '@visx/shape/lib/shapes/Pie';
import { scaleOrdinal, scaleTime, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { LegendOrdinal, LegendItem, LegendLabel } from '@visx/legend';
import { schemeSet2 } from 'd3-scale-chromatic';
import { AreaClosed, LinePath, Bar, Line } from '@visx/shape';
import { curveNatural } from '@visx/curve';
import { GridRows } from '@visx/grid';
import { withTooltip, TooltipWithBounds } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { LinearGradient } from '@visx/gradient';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { extent, max, bisector } from 'd3-array';
import { timeFormat } from 'd3-time-format';
import seedrandom from 'seedrandom';
import truncate from 'lodash-es/truncate';
import Slider from 'react-slick';
import sample from 'lodash-es/sample';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _excluded = ["width", "height", "data", "margin", "colors", "textColor", "fontSize"];
var defaultMargin = {
  top: 20,
  right: 20,
  bottom: 20,
  left: 20
};

var PieChart = function PieChart(_ref) {
  var width = _ref.width,
      height = _ref.height,
      data = _ref.data,
      _ref$margin = _ref.margin,
      margin = _ref$margin === void 0 ? defaultMargin : _ref$margin,
      _ref$colors = _ref.colors,
      colors = _ref$colors === void 0 ? schemeSet2 : _ref$colors,
      _ref$textColor = _ref.textColor,
      textColor = _ref$textColor === void 0 ? '#fff' : _ref$textColor,
      _ref$fontSize = _ref.fontSize,
      fontSize = _ref$fontSize === void 0 ? 16 : _ref$fontSize,
      other = _objectWithoutPropertiesLoose(_ref, _excluded);

  var frequency = function frequency(d) {
    return d.frequency;
  };

  var innerWidth = width - margin.left - margin.right;
  var innerHeight = height - margin.top - margin.bottom;
  var radius = Math.min(innerWidth, innerHeight) / 2;
  var centerY = innerHeight / 2;
  var centerX = innerWidth / 2;
  var pieScale = scaleOrdinal({
    domain: data.map(function (l) {
      return l.label;
    }),
    range: colors
  });
  var legendGlyphSize = 15;
  return React__default.createElement("div", null, React__default.createElement(LegendOrdinal, {
    scale: pieScale
  }, function (labels) {
    return React__default.createElement("div", {
      style: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }
    }, labels.map(function (label, i) {
      return React__default.createElement(LegendItem, {
        key: "legend-quantile-" + i,
        margin: "0 5px"
      }, React__default.createElement("svg", {
        width: legendGlyphSize,
        height: legendGlyphSize
      }, React__default.createElement("rect", {
        fill: label.value,
        width: legendGlyphSize,
        height: legendGlyphSize
      })), React__default.createElement(LegendLabel, {
        align: "left",
        margin: "0 0 0 4px"
      }, label.text));
    }));
  }), React__default.createElement("svg", {
    width: width,
    height: height
  }, React__default.createElement(Group, {
    top: centerY + margin.top,
    left: centerX + margin.left
  }, React__default.createElement(Pie, Object.assign({
    data: data,
    pieValue: frequency,
    outerRadius: radius
  }, other), function (pie) {
    return pie.arcs.map(function (arc, index) {
      var _arc$data = arc.data,
          value = _arc$data.value,
          label = _arc$data.label;

      var _pie$path$centroid = pie.path.centroid(arc),
          centroidX = _pie$path$centroid[0],
          centroidY = _pie$path$centroid[1];

      var hasSpaceForLabel = arc.endAngle - arc.startAngle >= 0.1;
      var arcPath = pie.path(arc) || '';
      var arcFill = pieScale(label);
      return React__default.createElement("g", {
        key: "arc-" + value + "-" + index
      }, React__default.createElement("path", {
        d: arcPath,
        fill: arcFill,
        "data-testid": "pie-path-" + index
      }), hasSpaceForLabel && React__default.createElement("text", {
        x: centroidX,
        y: centroidY,
        dy: ".33em",
        fill: textColor,
        fontSize: fontSize,
        textAnchor: "middle",
        pointerEvents: "none"
      }, value));
    });
  }))));
};

var _excluded$1 = ["fill"];

function SvgStarFilled(_ref) {
  var _ref$fill = _ref.fill,
      fill = _ref$fill === void 0 ? '#0095FF' : _ref$fill,
      others = _objectWithoutPropertiesLoose(_ref, _excluded$1);

  return React__default.createElement("svg", Object.assign({
    width: 16,
    height: 16
  }, others), React__default.createElement("defs", null, React__default.createElement("path", {
    d: "M5.663 5.438l1.654-3.981a.74.74 0 011.366 0l1.654 3.981 4.291.345a.742.742 0 01.422 1.302L11.78 9.89l1 4.194a.74.74 0 01-1.106.805L8 12.641 4.326 14.89a.74.74 0 01-1.106-.805l1-4.194L.95 7.085a.742.742 0 01.422-1.302l4.291-.345z",
    id: "Star-Filled_svg__a"
  })), React__default.createElement("use", {
    fill: fill,
    fillRule: "nonzero",
    xlinkHref: "#Star-Filled_svg__a"
  }));
}

var styles = {"tooltip":"ReviewsRatingChart-module_tooltip__3dgOn","tooltipReviews":"ReviewsRatingChart-module_tooltipReviews__1f4Gd","tooltipRating":"ReviewsRatingChart-module_tooltipRating__2eTaS"};

var tooltipHeight = 56;
var accentColor = '#2196F3';
var accentColorDark = '#0095FF'; // util

var formatDate = /*#__PURE__*/timeFormat('%B %d, %Y'); // accessors

var getDate = function getDate(d) {
  return new Date(d.label);
};

var getReviewsCount = function getReviewsCount(d) {
  return d.value;
};

var getRatingValue = function getRatingValue(d) {
  return d.rating;
};

var bisectDate = /*#__PURE__*/bisector(function (d) {
  return new Date(d.label);
}).left;
var ReviewsRatingChart = /*#__PURE__*/withTooltip(function (_ref) {
  var width = _ref.width,
      height = _ref.height,
      _ref$margin = _ref.margin,
      margin = _ref$margin === void 0 ? {
    top: 30,
    right: 24,
    bottom: 95,
    left: 105
  } : _ref$margin,
      showTooltip = _ref.showTooltip,
      hideTooltip = _ref.hideTooltip,
      tooltipData = _ref.tooltipData,
      _ref$tooltipTop = _ref.tooltipTop,
      tooltipTop = _ref$tooltipTop === void 0 ? 0 : _ref$tooltipTop,
      _ref$tooltipLeft = _ref.tooltipLeft,
      tooltipLeft = _ref$tooltipLeft === void 0 ? 0 : _ref$tooltipLeft,
      _ref$data = _ref.data,
      data = _ref$data === void 0 ? [] : _ref$data;
  if (width < 10) return null; // bounds

  var innerWidth = width - margin.left - margin.right;
  var innerHeight = height - margin.top - margin.bottom; // scales

  var dateScale = useMemo(function () {
    return scaleTime({
      range: [margin.left, innerWidth + margin.left],
      domain: extent(data, getDate)
    });
  }, [innerWidth, margin.left, data]);
  var ratingValueScale = useMemo(function () {
    return scaleLinear({
      range: [innerHeight + margin.top, margin.top],
      domain: [0, max(data, getReviewsCount) || 0]
    });
  }, [margin.top, innerHeight, data]); // tooltip handler

  var handleTooltip = useCallback(function (event) {
    var _ref2 = localPoint(event) || {
      x: 0
    },
        x = _ref2.x;

    var x0 = dateScale.invert(x);
    var index = bisectDate(data, x0, 1);
    var d0 = data[index - 1];
    var d1 = data[index];
    var d = d0;

    if (d1 && getDate(d1)) {
      d = x0.valueOf() - getDate(d0).valueOf() > getDate(d1).valueOf() - x0.valueOf() ? d1 : d0;
    }

    showTooltip({
      tooltipData: d,
      tooltipLeft: x,
      tooltipTop: ratingValueScale(getReviewsCount(d))
    });
  }, [showTooltip, ratingValueScale, dateScale, data]);
  return React__default.createElement("div", null, React__default.createElement("svg", {
    width: width,
    height: height
  }, React__default.createElement(LinearGradient, {
    id: "area-gradient",
    from: accentColor,
    to: "#fff",
    fromOpacity: 0.17,
    toOpacity: 0.1
  }), React__default.createElement(GridRows, {
    left: margin.left,
    scale: ratingValueScale,
    width: innerWidth,
    strokeDasharray: "3",
    stroke: "#E2E9F0",
    strokeOpacity: 1,
    strokeWidth: 2,
    pointerEvents: "none",
    numTicks: 6
  }), React__default.createElement(AreaClosed, {
    data: data,
    x: function x(d) {
      var _dateScale;

      return (_dateScale = dateScale(getDate(d))) != null ? _dateScale : 0;
    },
    y: function y(d) {
      var _ratingValueScale;

      return (_ratingValueScale = ratingValueScale(getReviewsCount(d))) != null ? _ratingValueScale : 0;
    },
    yScale: ratingValueScale,
    strokeWidth: 0,
    fill: "url(#area-gradient)",
    curve: curveNatural
  }), React__default.createElement(LinePath, {
    data: data,
    x: function x(d) {
      var _dateScale2;

      return (_dateScale2 = dateScale(getDate(d))) != null ? _dateScale2 : 0;
    },
    y: function y(d) {
      var _ratingValueScale2;

      return (_ratingValueScale2 = ratingValueScale(getReviewsCount(d))) != null ? _ratingValueScale2 : 0;
    },
    curve: curveNatural,
    stroke: "#0095FF",
    strokeWidth: 3
  }), React__default.createElement(AxisBottom, {
    top: innerHeight + margin.top + 12,
    scale: dateScale,
    // tickFormat={formatDate}
    stroke: "#000",
    tickStroke: "#000",
    tickLabelProps: function tickLabelProps() {
      return {
        fill: '#A1A3A5',
        fontSize: 12,
        fontWeight: 600,
        textAnchor: 'middle',
        verticalAnchor: 'start'
      };
    },
    labelProps: {
      fill: '#141A1E',
      fontSize: 12,
      fontWeight: 600,
      textAnchor: 'end',
      verticalAnchor: 'start'
    },
    label: "Months",
    labelOffset: 16,
    hideAxisLine: true,
    hideTicks: true
  }), React__default.createElement(AxisLeft, {
    scale: ratingValueScale,
    left: margin.left - 28,
    stroke: "#000",
    tickStroke: "#000",
    tickLabelProps: function tickLabelProps() {
      return {
        fill: '#A1A3A5',
        fontSize: 12,
        fontWeight: 600,
        textAnchor: 'middle',
        verticalAnchor: 'middle'
      };
    },
    labelProps: {
      fill: '#141A1E',
      fontSize: 12,
      fontWeight: 600,
      textAnchor: 'middle',
      verticalAnchor: 'start'
    },
    label: "Number of Reviews",
    numTicks: 6,
    hideAxisLine: true,
    hideTicks: true
  }), React__default.createElement(Bar, {
    x: margin.left,
    y: margin.top,
    width: innerWidth,
    height: innerHeight,
    fill: "transparent",
    rx: 14,
    onTouchStart: handleTooltip,
    onTouchMove: handleTooltip,
    onMouseMove: handleTooltip,
    onMouseLeave: function onMouseLeave() {
      return hideTooltip();
    }
  }), tooltipData && React__default.createElement("g", null, React__default.createElement(Line, {
    from: {
      x: tooltipLeft,
      y: margin.top
    },
    to: {
      x: tooltipLeft,
      y: innerHeight + margin.top
    },
    stroke: accentColorDark,
    strokeWidth: 2,
    pointerEvents: "none",
    strokeDasharray: "5,2"
  }), React__default.createElement("circle", {
    cx: tooltipLeft,
    cy: tooltipTop,
    r: 4,
    fill: accentColorDark,
    stroke: "white",
    strokeWidth: 2,
    pointerEvents: "none"
  }))), tooltipData && React__default.createElement("div", null, React__default.createElement(TooltipWithBounds, {
    top: tooltipTop - 12 - tooltipHeight / 2,
    left: tooltipLeft,
    className: styles.tooltip,
    applyPositionStyle: true,
    unstyled: true
  }, React__default.createElement("div", null, React__default.createElement("p", null, formatDate(getDate(tooltipData))), React__default.createElement("div", {
    className: styles.tooltipReviews
  }, React__default.createElement("span", null, getReviewsCount(tooltipData), " Reviews"), React__default.createElement("div", {
    className: styles.tooltipRating
  }, React__default.createElement(SvgStarFilled, {
    fill: "#FFCF33",
    width: 12,
    height: 12,
    viewBox: "0 0 16 16"
  }), getRatingValue(tooltipData)))))));
});

var _excluded$2 = ["fill", "clipFill", "clipX"];

function SvgStarFilledClipped(_ref) {
  var _ref$fill = _ref.fill,
      fill = _ref$fill === void 0 ? '#0095FF' : _ref$fill,
      _ref$clipFill = _ref.clipFill,
      clipFill = _ref$clipFill === void 0 ? '#0095FF' : _ref$clipFill,
      _ref$clipX = _ref.clipX,
      clipX = _ref$clipX === void 0 ? '100%' : _ref$clipX,
      others = _objectWithoutPropertiesLoose(_ref, _excluded$2);

  return React__default.createElement("svg", Object.assign({
    width: 16,
    height: 16
  }, others), React__default.createElement("defs", null, React__default.createElement("clipPath", {
    id: "Star-Filled-Clip"
  }, React__default.createElement("path", {
    d: "M5.663 5.438l1.654-3.981a.74.74 0 011.366 0l1.654 3.981 4.291.345a.742.742 0 01.422 1.302L11.78 9.89l1 4.194a.74.74 0 01-1.106.805L8 12.641 4.326 14.89a.74.74 0 01-1.106-.805l1-4.194L.95 7.085a.742.742 0 01.422-1.302l4.291-.345z",
    id: "Star-Filled-Clipped_svg__a"
  }))), React__default.createElement("use", {
    fill: fill,
    fillRule: "nonzero",
    xlinkHref: "#Star-Filled-Clipped_svg__a"
  }), React__default.createElement("rect", {
    x: clipX,
    y: "0",
    fill: clipFill,
    width: "100%",
    height: "100%",
    clipPath: "url(#Star-Filled-Clip)"
  }));
}

var _excluded$3 = ["rating", "containerClassName", "fillActive", "fillInactive", "starsTotal"];

var StarRating = function StarRating(_ref) {
  var rating = _ref.rating,
      containerClassName = _ref.containerClassName,
      _ref$fillActive = _ref.fillActive,
      fillActive = _ref$fillActive === void 0 ? '#FFCF33' : _ref$fillActive,
      _ref$fillInactive = _ref.fillInactive,
      fillInactive = _ref$fillInactive === void 0 ? '#F5F5F5' : _ref$fillInactive,
      _ref$starsTotal = _ref.starsTotal,
      starsTotal = _ref$starsTotal === void 0 ? 5 : _ref$starsTotal,
      svgProps = _objectWithoutPropertiesLoose(_ref, _excluded$3);

  var ratingFloor = Math.floor(rating);
  var clipX = 100;

  if (rating !== ratingFloor) {
    var percentage = (rating - ratingFloor) * 100;
    clipX = 100 - percentage;
  }

  return React__default.createElement("div", {
    className: containerClassName
  }, Array.from(Array(starsTotal)).map(function (_val, i) {
    return React__default.createElement(SvgStarFilledClipped, Object.assign({
      fill: i < ratingFloor ? fillActive : fillInactive,
      clipFill: fillActive,
      width: 50,
      height: 50,
      viewBox: "0 0 16 16",
      key: i,
      clipX: i === ratingFloor ? "-" + clipX + "%" : undefined,
      "data-testid": "star-" + i
    }, svgProps));
  }));
};

var tooltipHeight$1 = 56;
var accentColor$1 = '#2196F3';
var accentColorDark$1 = '#0095FF'; // util

var formatDate$1 = /*#__PURE__*/timeFormat('%B %d, %Y'); // accessors

var getDate$1 = function getDate(d) {
  return new Date(d.label);
};

var getReviewsCount$1 = function getReviewsCount(d) {
  return d.value;
};

var getRatingValue$1 = function getRatingValue(d) {
  return d.rating;
};

var bisectDate$1 = /*#__PURE__*/bisector(function (d) {
  return new Date(d.label);
}).left;
var ReviewsRatingChartMini = /*#__PURE__*/withTooltip(function (_ref) {
  var width = _ref.width,
      height = _ref.height,
      _ref$margin = _ref.margin,
      margin = _ref$margin === void 0 ? {
    top: 4,
    right: 0,
    bottom: 4,
    left: 0
  } : _ref$margin,
      showTooltip = _ref.showTooltip,
      hideTooltip = _ref.hideTooltip,
      tooltipData = _ref.tooltipData,
      _ref$tooltipTop = _ref.tooltipTop,
      tooltipTop = _ref$tooltipTop === void 0 ? 0 : _ref$tooltipTop,
      _ref$tooltipLeft = _ref.tooltipLeft,
      tooltipLeft = _ref$tooltipLeft === void 0 ? 0 : _ref$tooltipLeft,
      _ref$data = _ref.data,
      data = _ref$data === void 0 ? [] : _ref$data;
  if (width < 10) return null; // bounds

  var innerWidth = width - margin.left - margin.right;
  var innerHeight = height - margin.top - margin.bottom; // scales

  var dateScale = useMemo(function () {
    return scaleTime({
      range: [margin.left, innerWidth + margin.left],
      domain: extent(data, getDate$1)
    });
  }, [innerWidth, margin.left, data]);
  var ratingValueScale = useMemo(function () {
    return scaleLinear({
      range: [innerHeight + margin.top, margin.top],
      domain: [0, max(data, getReviewsCount$1) || 0]
    });
  }, [margin.top, innerHeight, data]); // tooltip handler

  var handleTooltip = useCallback(function (event) {
    var _ref2 = localPoint(event) || {
      x: 0
    },
        x = _ref2.x;

    var x0 = dateScale.invert(x);
    var index = bisectDate$1(data, x0, 1);
    var d0 = data[index - 1];
    var d1 = data[index];
    var d = d0;

    if (d1 && getDate$1(d1)) {
      d = x0.valueOf() - getDate$1(d0).valueOf() > getDate$1(d1).valueOf() - x0.valueOf() ? d1 : d0;
    }

    showTooltip({
      tooltipData: d,
      tooltipLeft: x,
      tooltipTop: ratingValueScale(getReviewsCount$1(d))
    });
  }, [showTooltip, ratingValueScale, dateScale, data]);
  return React__default.createElement("div", null, React__default.createElement("svg", {
    width: width,
    height: height
  }, React__default.createElement(LinearGradient, {
    id: "area-gradient",
    from: accentColor$1,
    to: "#fff",
    fromOpacity: 0.17,
    toOpacity: 0.1
  }), React__default.createElement(AreaClosed, {
    data: data,
    x: function x(d) {
      var _dateScale;

      return (_dateScale = dateScale(getDate$1(d))) != null ? _dateScale : 0;
    },
    y: function y(d) {
      var _ratingValueScale;

      return (_ratingValueScale = ratingValueScale(getReviewsCount$1(d))) != null ? _ratingValueScale : 0;
    },
    yScale: ratingValueScale,
    strokeWidth: 0,
    fill: "url(#area-gradient)",
    curve: curveNatural
  }), React__default.createElement(LinePath, {
    data: data,
    x: function x(d) {
      var _dateScale2;

      return (_dateScale2 = dateScale(getDate$1(d))) != null ? _dateScale2 : 0;
    },
    y: function y(d) {
      var _ratingValueScale2;

      return (_ratingValueScale2 = ratingValueScale(getReviewsCount$1(d))) != null ? _ratingValueScale2 : 0;
    },
    curve: curveNatural,
    stroke: "#0095FF",
    strokeWidth: 3
  }), React__default.createElement(Bar, {
    x: margin.left,
    y: margin.top,
    width: innerWidth,
    height: innerHeight,
    fill: "transparent",
    rx: 14,
    onTouchStart: handleTooltip,
    onTouchMove: handleTooltip,
    onMouseMove: handleTooltip,
    onMouseLeave: function onMouseLeave() {
      return hideTooltip();
    }
  }), tooltipData && React__default.createElement("g", null, React__default.createElement(Line, {
    from: {
      x: tooltipLeft,
      y: margin.top
    },
    to: {
      x: tooltipLeft,
      y: innerHeight + margin.top
    },
    stroke: accentColorDark$1,
    strokeWidth: 2,
    pointerEvents: "none",
    strokeDasharray: "5,2"
  }), React__default.createElement("circle", {
    cx: tooltipLeft,
    cy: tooltipTop,
    r: 4,
    fill: accentColorDark$1,
    stroke: "white",
    strokeWidth: 2,
    pointerEvents: "none"
  }))), tooltipData && React__default.createElement("div", null, React__default.createElement(TooltipWithBounds, {
    top: tooltipTop - 12 - tooltipHeight$1 / 2,
    left: tooltipLeft,
    className: styles.tooltip,
    applyPositionStyle: true,
    unstyled: true
  }, React__default.createElement("div", null, React__default.createElement("p", null, formatDate$1(getDate$1(tooltipData))), React__default.createElement("div", {
    className: styles.tooltipReviews
  }, React__default.createElement("span", null, getReviewsCount$1(tooltipData), " Reviews"), React__default.createElement("div", {
    className: styles.tooltipRating
  }, React__default.createElement(SvgStarFilled, {
    fill: "#FFCF33",
    width: 12,
    height: 12,
    viewBox: "0 0 16 16"
  }), getRatingValue$1(tooltipData)))))));
});

var FunnelChart = function FunnelChart(_ref) {
  var data = _ref.data,
      width = _ref.width,
      _ref$strokeColor = _ref.strokeColor,
      strokeColor = _ref$strokeColor === void 0 ? '#fff' : _ref$strokeColor,
      _ref$strokeWidth = _ref.strokeWidth,
      strokeWidth = _ref$strokeWidth === void 0 ? 10 : _ref$strokeWidth;
  var chartRef = useRef(null);
  var options = {
    block: {
      dynamicHeight: true,
      minHeight: 50
    }
  };
  useEffect(function () {
    Promise.all([import('d3-funnel'), import('d3-selection')]).then(function (_ref2) {
      var D3Funnel = _ref2[0],
          D3Selection = _ref2[1];
      var chart = new D3Funnel.default(chartRef.current);
      chart.draw(data, options);
      D3Selection.selectAll('.funnel-chart g path').attr('stroke', strokeColor).attr('stroke-width', strokeWidth);
    });
  }, [width]);
  return React__default.createElement("div", {
    className: "funnel-chart",
    ref: chartRef
  });
};

var styles$1 = {"container":"StarRatingDistribution-module_container__2oPFp","label":"StarRatingDistribution-module_label__1M96T","bar":"StarRatingDistribution-module_bar__1CHmz","barFill":"StarRatingDistribution-module_barFill__3FXDK","value":"StarRatingDistribution-module_value__3WGqG"};

var StarRatingDistribution = function StarRatingDistribution(_ref) {
  var label = _ref.label,
      value = _ref.value,
      customStyle = _ref.customStyle,
      _ref$starFill = _ref.starFill,
      starFill = _ref$starFill === void 0 ? '#FFCF33' : _ref$starFill,
      _ref$barColor = _ref.barColor,
      barColor = _ref$barColor === void 0 ? '#e2e9f0' : _ref$barColor,
      _ref$barFillColor = _ref.barFillColor,
      barFillColor = _ref$barFillColor === void 0 ? '#0095ff' : _ref$barFillColor;
  return React__default.createElement("div", {
    className: styles$1.container,
    style: customStyle
  }, React__default.createElement("div", {
    className: styles$1.label
  }, React__default.createElement("span", null, label), React__default.createElement(SvgStarFilled, {
    fill: starFill,
    width: 12,
    height: 12,
    viewBox: "0 0 16 16"
  })), React__default.createElement("div", {
    className: styles$1.bar,
    style: {
      background: barColor
    }
  }, React__default.createElement("div", {
    className: styles$1.barFill,
    style: {
      width: value,
      background: barFillColor
    }
  })), React__default.createElement("span", {
    className: styles$1.value
  }, value));
};

var LetterAvatar = function LetterAvatar(_ref) {
  var name = _ref.name,
      className = _ref.className;
  var colorPalette = ['#264653', '#2a9d8f', '#e9c46a', '#f4a261', '#e76f51'];

  var getColor = function getColor(seed) {
    var min = 0;
    var max = colorPalette.length - 1;
    var rng = seedrandom(seed);
    var colorIndex = Math.floor(rng.quick() * (max - min + 1) + min);
    return colorPalette[colorIndex];
  };

  return React__default.createElement("div", {
    style: {
      backgroundColor: getColor(name)
    },
    className: className
  }, name == null ? void 0 : name.charAt(0));
};

var locales = {
  EN: {
    langCode: 'en-US',
    basedOnCount: function basedOnCount(count) {
      return "Based on " + count + " reviews";
    },
    readMore: 'Read more',
    hide: 'Hide'
  },
  DE: {
    langCode: 'de-DE',
    basedOnCount: function basedOnCount(count) {
      return "Basierend auf " + count + " Bewertungen";
    },
    readMore: 'Weiterlesen',
    hide: 'Verstecken'
  },
  DK: {
    langCode: 'da-DK',
    basedOnCount: function basedOnCount(count) {
      return "Baseret p\xE5 " + count + " anmeldelser";
    },
    readMore: 'Læs mere',
    hide: 'Skjule'
  },
  ES: {
    langCode: 'es-ES',
    basedOnCount: function basedOnCount(count) {
      return "Basado en " + count + " opiniones";
    },
    readMore: 'Lee mas',
    hide: 'Esconder'
  },
  FR: {
    langCode: 'fr-FR',
    basedOnCount: function basedOnCount(count) {
      return "Bas\xE9 sur " + count + " avis";
    },
    readMore: 'Lire la suite',
    hide: 'Cacher'
  },
  NL: {
    langCode: 'nl-NL',
    basedOnCount: function basedOnCount(count) {
      return "Gebaseerd op " + count + " beoordelingen";
    },
    readMore: 'Lees verder',
    hide: 'Verbergen'
  }
};

var Head = function Head(_ref) {
  var rating = _ref.rating,
      userPic = _ref.userPic,
      sourcePic = _ref.sourcePic,
      userName = _ref.userName,
      date = _ref.date,
      displayElements = _ref.displayElements,
      locale = _ref.locale;
  var dateOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  var parsedDate = new Date(date).toLocaleDateString(locales[locale].langCode, dateOptions);

  var _useState = useState(userPic),
      currentSrc = _useState[0],
      setCurrentSrc = _useState[1];

  var handleError = function handleError() {
    setCurrentSrc(false);
  };

  return React__default.createElement("div", {
    className: "v2-widget-head"
  }, displayElements.image && React__default.createElement("div", {
    className: "v2-widget-head-avatar-container"
  }, currentSrc && typeof currentSrc === 'string' ? React__default.createElement("img", {
    className: "v2-widget-head-avatar",
    src: currentSrc,
    onError: handleError,
    alt: "Avatar"
  }) : React__default.createElement(LetterAvatar, {
    name: userName,
    className: "v2-widget-head-avatar"
  }), React__default.createElement("img", {
    className: "v2-widget-head-source",
    src: sourcePic,
    alt: "Source"
  })), React__default.createElement("div", {
    className: "v2-widget-head-info"
  }, displayElements.name && React__default.createElement("p", {
    className: "v2-widget-head-username"
  }, userName), React__default.createElement("div", {
    className: "v2-widget-head-rating"
  }, displayElements.rating && React__default.createElement(StarRating, {
    rating: rating,
    width: 16,
    height: 16
  }), displayElements.date && React__default.createElement("span", {
    className: "v2-widget-head-date"
  }, parsedDate))));
};

var Text = function Text(_ref) {
  var text = _ref.text,
      _ref$length = _ref.length,
      length = _ref$length === void 0 ? 150 : _ref$length,
      locale = _ref.locale,
      linkColor = _ref.linkColor;

  var _useState = useState(false),
      showFullText = _useState[0],
      setShowFullText = _useState[1];

  var handleClick = function handleClick() {
    setShowFullText(!showFullText);
  };

  var createMarkup = function createMarkup() {
    var richText = !showFullText ? truncate(text, {
      length: length,
      separator: ' '
    }) : text;
    return {
      __html: richText
    };
  };

  return (text == null ? void 0 : text.length) > length ? React__default.createElement(React__default.Fragment, null, React__default.createElement("span", {
    dangerouslySetInnerHTML: createMarkup()
  }), ' ', React__default.createElement("button", {
    className: "v2-widget-text-toggle",
    onClick: handleClick,
    style: {
      color: linkColor
    }
  }, !showFullText ? locales[locale].readMore : locales[locale].hide)) : React__default.createElement("span", {
    dangerouslySetInnerHTML: createMarkup()
  });
};

var _excluded$4 = ["fill"];

function SvgArrowLeft(_ref) {
  var _ref$fill = _ref.fill,
      fill = _ref$fill === void 0 ? '#0095FF' : _ref$fill,
      others = _objectWithoutPropertiesLoose(_ref, _excluded$4);

  return createElement("svg", Object.assign({
    width: 16,
    height: 16
  }, others), createElement("path", {
    d: "M7.28 2.47a.75.75 0 00-.976-.073l-.084.073-5 5-.036.038-.056.072-.054.095-.035.086-.027.103-.01.084L1 8l.003.07.017.101.019.068.023.06.035.07.032.051.027.038.064.072 5 5a.75.75 0 001.133-.976l-.073-.084-3.719-3.72H14.25a.75.75 0 100-1.5H3.561l3.72-3.72a.75.75 0 00.072-.976L7.28 2.47z",
    fill: fill,
    fillRule: "evenodd"
  }));
}

var _excluded$5 = ["fill"];

function SvgArrowRight(_ref) {
  var _ref$fill = _ref.fill,
      fill = _ref$fill === void 0 ? '#0095FF' : _ref$fill,
      others = _objectWithoutPropertiesLoose(_ref, _excluded$5);

  return createElement("svg", Object.assign({
    width: 16,
    height: 16
  }, others), createElement("path", {
    d: "M8.72 2.47a.75.75 0 01.976-.073l.084.073 5 5 .036.038.056.072.054.095.035.086.027.103.01.084v.103l-.018.12-.019.068-.023.06-.035.07-.032.051-.027.038-.064.072-5 5a.75.75 0 01-1.133-.976l.073-.084 3.719-3.72H1.75a.75.75 0 010-1.5h10.689l-3.72-3.72a.75.75 0 01-.072-.976l.073-.084z",
    fill: fill,
    fillRule: "evenodd"
  }));
}

var _excluded$6 = ["fill"];

function LoadingIndicator(_ref) {
  var _ref$fill = _ref.fill,
      fill = _ref$fill === void 0 ? '#ffffff' : _ref$fill,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded$6);

  return createElement("svg", Object.assign({
    width: 32,
    height: 32,
    viewBox: "0 0 128 128",
    "data-testid": "loading-indicator"
  }, rest), createElement("rect", {
    x: "0",
    y: "0",
    width: "100%",
    height: "100%",
    fill: fill
  }), createElement("g", null, createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#0095ff",
    fillOpacity: "1"
  }), createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#cceaff",
    fillOpacity: "0.2",
    transform: "rotate(30 64 64)"
  }), createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#cceaff",
    fillOpacity: "0.2",
    transform: "rotate(60 64 64)"
  }), createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#cceaff",
    fillOpacity: "0.2",
    transform: "rotate(90 64 64)"
  }), createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#cceaff",
    fillOpacity: "0.2",
    transform: "rotate(120 64 64)"
  }), createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#b2dfff",
    fillOpacity: "0.3",
    transform: "rotate(150 64 64)"
  }), createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#99d5ff",
    fillOpacity: "0.4",
    transform: "rotate(180 64 64)"
  }), createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#7fcaff",
    fillOpacity: "0.5",
    transform: "rotate(210 64 64)"
  }), createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#66bfff",
    fillOpacity: "0.6",
    transform: "rotate(240 64 64)"
  }), createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#4cb5ff",
    fillOpacity: "0.7",
    transform: "rotate(270 64 64)"
  }), createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#33aaff",
    fillOpacity: "0.8",
    transform: "rotate(300 64 64)"
  }), createElement("path", {
    d: "M59.6 0h8v40h-8V0z",
    fill: "#199fff",
    fillOpacity: "0.9",
    transform: "rotate(330 64 64)"
  }), createElement("animateTransform", {
    attributeName: "transform",
    type: "rotate",
    values: "0 64 64;30 64 64;60 64 64;90 64 64;120 64 64;150 64 64;180 64 64;210 64 64;240 64 64;270 64 64;300 64 64;330 64 64",
    calcMode: "discrete",
    dur: "1080ms",
    repeatCount: "indefinite"
  })));
}

function SvgCloseCircle(props) {
  return createElement("svg", Object.assign({
    width: 16,
    height: 16
  }, props), createElement("path", {
    d: "M8 0a8 8 0 110 16A8 8 0 018 0zm2.95 5.05a.75.75 0 010 1.06L9.06 8l1.89 1.89a.75.75 0 01-1.06 1.06L8 9.06l-1.89 1.89a.75.75 0 01-1.06-1.06L6.94 8 5.05 6.11a.75.75 0 111.06-1.06L8 6.94l1.89-1.89a.75.75 0 011.06 0z",
    fill: "#a1a3a5",
    fillRule: "evenodd"
  }));
}

var _excluded$7 = ["fill"];

function SvgQuotes(_ref) {
  var _ref$fill = _ref.fill,
      fill = _ref$fill === void 0 ? '#0095FF' : _ref$fill,
      others = _objectWithoutPropertiesLoose(_ref, _excluded$7);

  return createElement("svg", Object.assign({
    width: 16,
    height: 16
  }, others), createElement("path", {
    d: "M14.5 2a1.5 1.5 0 01.145 2.993l-.392.02c-1.189.131-1.892.575-2.145 1.985L15.204 7c.44 0 .796.357.796.797v5.406c0 .44-.357.797-.796.797H9.8a.797.797 0 01-.796-.797V8.438c0-.216-.008-.429 0-.639C9.152 3.925 10.704 2 14.5 2zm-9 0a1.5 1.5 0 01.145 2.993l-.392.02c-1.189.131-1.892.575-2.145 1.985L6.204 7c.44 0 .796.357.796.797v5.406c0 .44-.357.797-.796.797H.8a.797.797 0 01-.796-.797V8.438c0-.216-.008-.429 0-.639C.152 3.925 1.704 2 5.5 2z",
    fill: fill,
    fillRule: "evenodd"
  }));
}

var Review = function Review(_ref) {
  var displayElements = _ref.displayElements,
      videoUrl = _ref.videoUrl,
      colors = _ref.colors,
      fonts = _ref.fonts,
      text = _ref.text,
      posterUrl = _ref.posterUrl,
      locale = _ref.locale;
  return React__default.createElement(React__default.Fragment, null, displayElements.text && text && !videoUrl && React__default.createElement("div", {
    className: "v2-widget-review-text",
    style: {
      color: colors.text,
      fontSize: fonts.size + "px",
      fontWeight: fonts.weight
    }
  }, displayElements.quote && React__default.createElement("div", null, React__default.createElement(SvgQuotes, {
    fill: "#a1a3a5",
    viewBox: "0 0 16 16",
    width: 24,
    height: 24
  })), React__default.createElement(Text, {
    text: text,
    locale: locale,
    linkColor: colors.link
  })), videoUrl && React__default.createElement("video", {
    className: "v2-widget-review-video",
    controls: true,
    poster: posterUrl,
    src: videoUrl
  }));
};

var useIntersectionObserver = function useIntersectionObserver(loaderRef, callback, changeIndicator) {
  var options = {
    root: null,
    rootMargin: '0px',
    threshold: 1
  };
  useEffect(function () {
    var observerCallback = function observerCallback(entries, observer) {
      var entry = entries[0];

      if (entry.isIntersecting) {
        callback();
        observer.unobserve(loaderRef.current);
      }
    };

    var observer = new IntersectionObserver(observerCallback, options);

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return function () {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [loaderRef, changeIndicator]);
};

var List = function List(_ref) {
  var reviews = _ref.reviews,
      data = _ref.data,
      onLoadMore = _ref.onLoadMore,
      loading = _ref.loading;
  var loaderRef = useRef(null);
  useIntersectionObserver(loaderRef, onLoadMore, reviews);
  return React__default.createElement("div", {
    className: "v2-widget-list",
    style: {
      background: data.colors.bg
    }
  }, !(reviews != null && reviews.length) && !loading && React__default.createElement("p", null, "No reviews to show"), React__default.createElement("ul", null, reviews == null ? void 0 : reviews.map(function (_ref2, i) {
    var rating = _ref2.rating,
        userPic = _ref2.userPic,
        sourcePic = _ref2.reviewSource.sourcePic,
        userName = _ref2.userName,
        date = _ref2.date,
        text = _ref2.text,
        videoUrl = _ref2.videoUrl,
        posterUrl = _ref2.posterUrl;
    return React__default.createElement("li", {
      key: "review-" + i,
      style: {
        background: data.colors.reviewBg
      }
    }, React__default.createElement(Head, {
      rating: rating,
      userPic: userPic,
      sourcePic: sourcePic,
      userName: userName,
      date: date,
      displayElements: data.displayElements,
      locale: data.locale
    }), React__default.createElement(Review, {
      text: text,
      videoUrl: videoUrl,
      posterUrl: posterUrl,
      colors: data.colors,
      fonts: data.fonts,
      displayElements: data.displayElements,
      locale: data.locale
    }));
  })), loading && React__default.createElement(LoadingIndicator, {
    fill: "transparent",
    className: "v2-widget-list-loading-indicator"
  }), React__default.createElement("div", {
    ref: loaderRef
  }));
};

var Grid = function Grid(_ref) {
  var reviews = _ref.reviews,
      data = _ref.data,
      onLoadMore = _ref.onLoadMore,
      loading = _ref.loading;
  var loaderRef = useRef(null);
  useIntersectionObserver(loaderRef, onLoadMore, reviews);
  return React__default.createElement("div", {
    className: "v2-widget-grid",
    style: {
      background: data.colors.bg
    }
  }, !(reviews != null && reviews.length) && !loading && React__default.createElement("p", null, "No reviews to show"), React__default.createElement("ul", null, reviews == null ? void 0 : reviews.map(function (_ref2, i) {
    var rating = _ref2.rating,
        userPic = _ref2.userPic,
        sourcePic = _ref2.reviewSource.sourcePic,
        userName = _ref2.userName,
        date = _ref2.date,
        text = _ref2.text,
        videoUrl = _ref2.videoUrl,
        posterUrl = _ref2.posterUrl;
    return React__default.createElement("li", {
      key: "review-" + i,
      style: {
        background: data.colors.reviewBg
      }
    }, React__default.createElement(Head, {
      rating: rating,
      userPic: userPic,
      sourcePic: sourcePic,
      userName: userName,
      date: date,
      displayElements: data.displayElements,
      locale: data.locale
    }), React__default.createElement(Review, {
      text: text,
      videoUrl: videoUrl,
      posterUrl: posterUrl,
      colors: data.colors,
      fonts: data.fonts,
      displayElements: data.displayElements,
      locale: data.locale
    }));
  })), loading && React__default.createElement(LoadingIndicator, {
    fill: "transparent",
    className: "v2-widget-grid-loading-indicator"
  }), React__default.createElement("div", {
    ref: loaderRef
  }));
};

var NextArrow = function NextArrow(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick;
  return React__default.createElement("div", {
    className: className,
    onClick: onClick
  }, React__default.createElement(SvgArrowRight, null));
};

var PrevArrow = function PrevArrow(_ref2) {
  var className = _ref2.className,
      onClick = _ref2.onClick;
  return React__default.createElement("div", {
    className: className,
    onClick: onClick
  }, React__default.createElement(SvgArrowLeft, null));
};

var Carousel = function Carousel(_ref3) {
  var _ref3$sliderSettings = _ref3.sliderSettings,
      sliderSettings = _ref3$sliderSettings === void 0 ? {} : _ref3$sliderSettings,
      reviews = _ref3.reviews,
      data = _ref3.data,
      onLoadMore = _ref3.onLoadMore,
      loading = _ref3.loading;

  var _useState = useState(0),
      activeSlide = _useState[0],
      setActiveSlide = _useState[1];

  var settings = _extends({
    dots: true,
    infinite: false,
    speed: 1000,
    slidesToShow: 4,
    slidesToScroll: 1,
    nextArrow: React__default.createElement(NextArrow, null),
    prevArrow: React__default.createElement(PrevArrow, null),
    // afterChange is broken https://github.com/akiran/react-slick/issues/1262
    beforeChange: function beforeChange(_current, next) {
      setTimeout(function () {
        return setActiveSlide(next);
      }, sliderSettings.speed || 1000);
    },
    responsive: [{
      breakpoint: 600,
      settings: {
        slidesToShow: 1
      }
    }, {
      breakpoint: 960,
      settings: {
        slidesToShow: 2
      }
    }, {
      breakpoint: 1320,
      settings: {
        slidesToShow: 3
      }
    }]
  }, sliderSettings);

  useEffect(function () {
    if (!loading && reviews != null && reviews.length && activeSlide >= reviews.length - settings.slidesToShow) {
      onLoadMore();
    }
  }, [activeSlide]);
  return React__default.createElement("div", {
    className: "v2-widget-carousel",
    style: {
      background: data.colors.bg
    }
  }, !(reviews != null && reviews.length) && !loading && React__default.createElement("p", null, "No reviews to show"), !!(reviews != null && reviews.length) && React__default.createElement(Slider, Object.assign({}, settings), reviews.map(function (_ref4, i) {
    var rating = _ref4.rating,
        userPic = _ref4.userPic,
        sourcePic = _ref4.reviewSource.sourcePic,
        userName = _ref4.userName,
        date = _ref4.date,
        text = _ref4.text,
        videoUrl = _ref4.videoUrl,
        posterUrl = _ref4.posterUrl;
    return React__default.createElement("div", {
      className: "v2-widget-carousel-item",
      key: "review-" + i
    }, React__default.createElement("div", {
      className: "v2-widget-carousel-item-card",
      style: {
        background: data.colors.reviewBg
      }
    }, React__default.createElement(Head, {
      rating: rating,
      userPic: userPic,
      sourcePic: sourcePic,
      userName: userName,
      date: date,
      displayElements: data.displayElements,
      locale: data.locale
    }), React__default.createElement(Review, {
      text: text,
      videoUrl: videoUrl,
      posterUrl: posterUrl,
      colors: data.colors,
      fonts: data.fonts,
      displayElements: data.displayElements,
      locale: data.locale
    })));
  })));
};

var Summary = function Summary(_ref) {
  var rating = _ref.rating,
      reviewsCount = _ref.reviewsCount,
      logos = _ref.logos,
      locale = _ref.locale,
      brandingLogo = _ref.brandingLogo,
      brandingName = _ref.brandingName;
  return React__default.createElement("div", {
    className: "v2-widget-summary"
  }, React__default.createElement("div", {
    className: "v2-widget-summary-data"
  }, React__default.createElement("div", {
    className: "v2-widget-summary-info"
  }, React__default.createElement("p", {
    className: "v2-widget-summary-rating"
  }, rating), React__default.createElement("p", {
    className: "v2-widget-summary-count"
  }, locales[locale].basedOnCount(reviewsCount)), React__default.createElement(StarRating, {
    rating: rating,
    width: 16,
    height: 16
  })), React__default.createElement("div", {
    className: "v2-widget-summary-logos"
  }, logos.map(function (logo, i) {
    return React__default.createElement("img", {
      key: "logo-" + i,
      src: logo,
      alt: "Provider"
    });
  }))), brandingLogo && React__default.createElement("div", {
    className: "v2-widget-summary-label"
  }, "Powered by", React__default.createElement("a", {
    href: "https://www.reviewshake.com/",
    target: "_blank",
    rel: "noopener"
  }, React__default.createElement("img", {
    alt: brandingName,
    src: brandingLogo
  }))));
};

var _excluded$8 = ["fill"];

function SvgArrowHalfDown(_ref) {
  var _ref$fill = _ref.fill,
      fill = _ref$fill === void 0 ? '#0095FF' : _ref$fill,
      others = _objectWithoutPropertiesLoose(_ref, _excluded$8);

  return createElement("svg", Object.assign({
    width: 16,
    height: 16
  }, others), createElement("defs", null, createElement("path", {
    d: "M8 9.44L3.53 4.97a.75.75 0 00-1.06 1.06l5 5a.75.75 0 001.06 0l5-5a.75.75 0 00-1.06-1.06L8 9.44z",
    id: "Arrow-Half-Down_svg__a"
  })), createElement("use", {
    fill: fill,
    xlinkHref: "#Arrow-Half-Down_svg__a",
    fillRule: "evenodd"
  }));
}

var FloatingBody = function FloatingBody(_ref) {
  var reviews = _ref.reviews,
      data = _ref.data,
      onLoadMore = _ref.onLoadMore,
      loading = _ref.loading,
      toggleMaximized = _ref.toggleMaximized;
  var loaderRef = useRef(null);
  useIntersectionObserver(loaderRef, onLoadMore, reviews);
  return React__default.createElement("div", {
    className: "v2-widget-floating-container"
  }, React__default.createElement("button", {
    className: "v2-widget-floating-toggle",
    onClick: toggleMaximized
  }, React__default.createElement(SvgArrowHalfDown, {
    fill: "#FFF"
  })), !(reviews != null && reviews.length) && !loading && React__default.createElement("p", null, "No reviews to show"), React__default.createElement("ul", null, reviews == null ? void 0 : reviews.map(function (_ref2, i) {
    var rating = _ref2.rating,
        userPic = _ref2.userPic,
        sourcePic = _ref2.reviewSource.sourcePic,
        userName = _ref2.userName,
        date = _ref2.date,
        text = _ref2.text,
        posterUrl = _ref2.posterUrl,
        videoUrl = _ref2.videoUrl;
    return React__default.createElement("li", {
      key: "review-" + i,
      style: {
        background: data.colors.reviewBg
      }
    }, React__default.createElement(Head, {
      rating: rating,
      userPic: userPic,
      sourcePic: sourcePic,
      userName: userName,
      date: date,
      displayElements: data.displayElements,
      locale: data.locale
    }), React__default.createElement(Review, {
      text: text,
      videoUrl: videoUrl,
      posterUrl: posterUrl,
      colors: data.colors,
      fonts: data.fonts,
      displayElements: data.displayElements,
      locale: data.locale
    }));
  })), loading && React__default.createElement(LoadingIndicator, {
    fill: "transparent",
    className: "v2-widget-floating-loading-indicator"
  }), React__default.createElement("div", {
    ref: loaderRef
  }));
};

var Floating = function Floating(_ref) {
  var reviews = _ref.reviews,
      data = _ref.data,
      onLoadMore = _ref.onLoadMore,
      loading = _ref.loading,
      brandingLogo = _ref.brandingLogo,
      brandingName = _ref.brandingName;

  var _useState = useState(false),
      isMaximized = _useState[0],
      setMaximized = _useState[1];

  var _useState2 = useState(true),
      isVisible = _useState2[0],
      setVisible = _useState2[1];

  var _useState3 = useState(null),
      sampleReview = _useState3[0],
      setSampleReview = _useState3[1];

  var toggleMaximized = function toggleMaximized() {
    setMaximized(!isMaximized);
  };

  var handleClose = function handleClose() {
    setVisible(false);
  };

  var maximizedClassName = isMaximized ? 'v2-widget-floating-maximized' : 'v2-widget-floating-minimized';

  var handleClickStopPropagation = function handleClickStopPropagation(e) {
    e.stopPropagation();
  };

  useEffect(function () {
    setSampleReview(sample(reviews));
  }, []);
  return isVisible ? React__default.createElement("div", {
    className: "v2-widget-floating " + maximizedClassName,
    onClick: !isMaximized ? toggleMaximized : null
  }, isMaximized ? React__default.createElement(FloatingBody, {
    reviews: reviews,
    data: data,
    onLoadMore: onLoadMore,
    loading: loading,
    toggleMaximized: toggleMaximized
  }) : React__default.createElement(React__default.Fragment, null, React__default.createElement(SvgCloseCircle, {
    width: 21,
    height: 21,
    viewBox: "0 0 16 16",
    onClick: handleClose,
    className: "v2-widget-floating-close-btn"
  }), React__default.createElement(Head, {
    rating: sampleReview == null ? void 0 : sampleReview.rating,
    userPic: sampleReview == null ? void 0 : sampleReview.userPic,
    sourcePic: sampleReview == null ? void 0 : sampleReview.reviewSource.sourcePic,
    userName: sampleReview == null ? void 0 : sampleReview.userName,
    date: sampleReview == null ? void 0 : sampleReview.date,
    displayElements: data.displayElements,
    locale: data.locale
  }), React__default.createElement(Review, {
    text: sampleReview == null ? void 0 : sampleReview.text,
    videoUrl: null,
    posterUrl: null,
    colors: data.colors,
    fonts: data.fonts,
    displayElements: data.displayElements,
    locale: data.locale
  }), brandingLogo && React__default.createElement("div", {
    className: "v2-widget-floating-label"
  }, "Powered by", React__default.createElement("a", {
    href: "https://www.reviewshake.com/",
    target: "_blank",
    rel: "noopener",
    onClick: handleClickStopPropagation
  }, React__default.createElement("img", {
    alt: brandingName,
    src: brandingLogo
  }))))) : null;
};

export { Carousel as CarouselWidget, Floating as FloatingWidget, FunnelChart, Grid as GridWidget, List as ListWidget, PieChart, ReviewsRatingChart, ReviewsRatingChartMini, StarRating, StarRatingDistribution, Summary as SummaryWidget };
//# sourceMappingURL=shake-ui.esm.js.map
